import { Link } from "@tanstack/react-router";
import { format } from "date-fns";

import { useTaskDetail, useUpdateTask } from "@/application/use-cases/tasks/use-tasks.queries";
import { useMeetingsForEntity } from "@/application/use-cases/meetings/use-meetings.queries";
import { useStakeholdersList } from "@/application/use-cases/stakeholders/use-stakeholders.queries";
import {
  PRIORITY_LABEL,
  StatusDot,
  TASK_STATUS_LABEL,
  priorityToTone,
  taskStatusToTone,
} from "@/presentation/components/status/status-dot";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";
import { Label } from "@monorepo-apps/ui/src/components/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@monorepo-apps/ui/src/components/select";
import type { Priority, TaskStatus } from "@/infrastructure/supabase/database.types";

export function TaskDrawerContent({ id }: { id: string }) {
  const taskQ = useTaskDetail(id);
  const meetingsQ = useMeetingsForEntity("task", id);
  const stakeholdersQ = useStakeholdersList();
  const updateTask = useUpdateTask();

  if (!taskQ.data) {
    return <p className="text-muted-foreground px-6 py-8 text-xs italic">Loading…</p>;
  }
  const t = taskQ.data;
  const stakeholders = stakeholdersQ.data ?? [];

  const dueDateInputValue = t.due_date ? new Date(t.due_date).toISOString().slice(0, 10) : "";

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header className="border-border/60 flex flex-col gap-1.5 border-b px-6 py-5">
        <span className="eyebrow inline-flex items-center gap-2">
          <StatusDot tone={taskStatusToTone(t.status)} /> {TASK_STATUS_LABEL[t.status]}
          <span className="text-border">·</span>
          <StatusDot tone={priorityToTone(t.priority)} /> {PRIORITY_LABEL[t.priority]}
        </span>
        <h2 className="text-base font-medium tracking-tight">{t.title}</h2>
      </header>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="flex flex-col gap-5">
          <Field label="Title">
            <Input
              defaultValue={t.title}
              onBlur={(e) =>
                e.target.value !== t.title &&
                e.target.value.trim() &&
                updateTask.mutate({ id: t.id, patch: { title: e.target.value.trim() } })
              }
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Status">
              <Select
                value={t.status}
                onValueChange={(v) =>
                  updateTask.mutate({ id: t.id, patch: { status: v as TaskStatus } })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todo">To do</SelectItem>
                  <SelectItem value="in_progress">In progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Priority">
              <Select
                value={t.priority}
                onValueChange={(v) =>
                  updateTask.mutate({ id: t.id, patch: { priority: v as Priority } })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Assignee">
              <Select
                value={t.assignee_id ?? "__none__"}
                onValueChange={(v) =>
                  updateTask.mutate({
                    id: t.id,
                    patch: { assignee_id: v === "__none__" ? null : v },
                  })
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Unassigned</SelectItem>
                  {stakeholders.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{" "}
                      <span className="text-muted-foreground text-[10px] uppercase">· {s.org}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Due date">
              <Input
                type="date"
                defaultValue={dueDateInputValue}
                onBlur={(e) => {
                  const next = e.target.value ? new Date(e.target.value).toISOString() : null;
                  if (next !== t.due_date) {
                    updateTask.mutate({ id: t.id, patch: { due_date: next } });
                  }
                }}
              />
            </Field>
          </div>

          <Field label="Description">
            <Textarea
              defaultValue={t.description ?? ""}
              rows={4}
              onBlur={(e) =>
                e.target.value !== (t.description ?? "") &&
                updateTask.mutate({ id: t.id, patch: { description: e.target.value } })
              }
            />
          </Field>

          <Field label="Discussed in">
            {meetingsQ.data && meetingsQ.data.length > 0 ? (
              <ul className="flex flex-col gap-1 text-sm">
                {meetingsQ.data.map((m) => (
                  <li key={m.id}>
                    <Link to="/meetings/$id" params={{ id: m.id }} className="hover:underline">
                      <span className="text-muted-foreground text-[11px]">
                        {format(new Date(m.meeting_at), "d MMM")} ·{" "}
                      </span>
                      {m.title}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted-foreground text-xs italic">
                Not yet discussed in any logged meeting.
              </p>
            )}
          </Field>

          <p className="text-muted-foreground border-border/40 mt-2 border-t pt-3 font-mono text-[10px] tracking-wider">
            Last updated {format(new Date(t.updated_at), "d MMM, HH:mm")}
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-muted-foreground text-[10px] tracking-wider uppercase">{label}</Label>
      {children}
    </div>
  );
}
